import { useContext } from 'react';

import { Trans } from '@lingui/react/macro';
import classNames from 'classnames';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { modelsTogglesAtom } from '@/modules/annotator/findingSidebar/atoms';
import {
  isPreviewOpenAtom,
  previewModeAtom,
  qrCodeDataAtom,
  qrCodeErrorAtom,
  qrCodeLoadingAtom,
  showQRCodeAtom,
} from '@/modules/annotator/multiFileViewer/imageToolbar/atoms';

import type { VisitIdToImagesMap } from '../hooks/usePatientPrintoutData';

import { PreviewSidebarActionButtons } from './PreviewActionButtons';
import { PreviewSidebarConfiguration } from './PreviewSidebarConfiguration';
import { PreviewSidebarQR } from './PreviewSidebarQR';
import { ThemeModeContext } from './QRPrintDownloadContent';

interface PreviewSidebarProps {
  componentRef: React.RefObject<HTMLDivElement>;
  patientName?: string;
}

export const PreviewSidebar = ({
  componentRef,
  patientName,
}: PreviewSidebarProps) => {
  const mode = useContext(ThemeModeContext);
  const previewMode = useAtomValue(previewModeAtom);
  const showQRCode = useAtomValue(showQRCodeAtom);
  const qrCodeData = useAtomValue(qrCodeDataAtom);
  const isLoadingQRCode = useAtomValue(qrCodeLoadingAtom);
  const setIsPreviewOpen = useSetAtom(isPreviewOpenAtom);
  const qrCodeError = useAtomValue(qrCodeErrorAtom);

  const [enabledFindingTypes, setEnabledFindingTypes] =
    useAtom(modelsTogglesAtom);

  // Show loading state while generating QR code
  if (previewMode === 'qrCode' && isLoadingQRCode) {
    return (
      <div
        className={classNames('vai-w-[28%] vai-flex-1', {
          'vai-border-l vai-border-l-sidebar-border vai-bg-sidebar-background vai-text-foreground-dark':
            mode === 'dark',
        })}
      >
        <div className="vai-flex vai-flex-col vai-h-full vai-w-full">
          <div className="vai-flex-1 vai-flex vai-items-center vai-justify-center">
            <div className="vai-text-center">
              <div className="vai-animate-spin vai-rounded-full vai-h-8 vai-w-8 vai-border-2 vai-border-t-transparent vai-border-sapphire-600 vai-mx-auto vai-mb-4"></div>
              <p
                className={classNames('vai-text-sm', {
                  'vai-text-foreground-dark': mode === 'dark',
                  'vai-text-zinc-900': mode === 'light',
                })}
              >
                Generating QR Code...
              </p>
            </div>
          </div>
          <PreviewSidebarActionButtons
            componentRef={componentRef}
            patientName={patientName}
            setIsPreviewOpen={setIsPreviewOpen}
          />
        </div>
      </div>
    );
  }

  // Show error state when QR code generation fails
  if (previewMode === 'qrCode' && showQRCode && qrCodeError) {
    return (
      <div
        className={classNames('vai-w-[28%] vai-flex-1', {
          'vai-border-l vai-border-l-sidebar-border vai-bg-sidebar-background vai-text-foreground-dark':
            mode === 'dark',
        })}
      >
        <div className="vai-flex vai-flex-col vai-h-full vai-w-full">
          <div className="vai-flex-1 vai-flex vai-items-center vai-justify-center">
            <div className="vai-text-center vai-p-4">
              <div className="vai-w-16 vai-h-16 vai-mx-auto vai-mb-4 vai-bg-red-100 vai-rounded-full vai-flex vai-items-center vai-justify-center">
                <svg
                  className="vai-w-8 vai-h-8 vai-text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="vai-text-lg vai-font-semibold vai-text-red-600 vai-mb-2">
                <Trans>QR Code Generation Failed</Trans>
              </h3>
              <p
                className={classNames('vai-text-sm vai-mb-4', {
                  'vai-text-zinc-900': mode === 'light',
                  'vai-text-foreground-dark': mode === 'dark',
                })}
              >
                <Trans>
                  Please try again or contact support if the problem persists.
                </Trans>
              </p>
            </div>
          </div>
          <PreviewSidebarActionButtons
            componentRef={componentRef}
            patientName={patientName}
            setIsPreviewOpen={setIsPreviewOpen}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames('vai-w-[28%]', {
        'vai-border-l vai-border-l-sidebar-border vai-bg-sidebar-background vai-text-foreground-dark':
          mode === 'dark',
      })}
    >
      <div className="vai-flex vai-flex-col vai-h-full vai-w-full vai-overflow-hidden">
        {previewMode === 'qrCode' && showQRCode && qrCodeData ? (
          <div className="vai-flex-1 vai-overflow-y-auto">
            <PreviewSidebarQR qrCodeData={qrCodeData} />
          </div>
        ) : (
          <PreviewSidebarConfiguration
            enabledFindingTypes={enabledFindingTypes}
            setEnabledFindingTypes={setEnabledFindingTypes}
          />
        )}
        <PreviewSidebarActionButtons
          componentRef={componentRef}
          patientName={patientName}
          setIsPreviewOpen={setIsPreviewOpen}
        />
      </div>
    </div>
  );
};
